import React, { useCallback } from 'react';
import { RefreshControl, StyleSheet } from 'react-native';

import { Scroll, Stack } from '@/components/layout';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

export type DetailInfoTemplateProps = {
  header: React.ReactNode;
  top: React.ReactNode;
  tab?: React.ReactNode;
  mobileContent: React.ReactNode;
  tabletLeftContent: React.ReactNode;
  tabletRightContent: React.ReactNode;
  onRefresh?: () => Promise<void> | void;
  isMobileRefreshing?: boolean;
  isTabletRefreshing?: boolean;
  tabletLeftPaneWidth?: number;
  mobileStickyHeaderIndices?: number[];
};

export function DetailInfoTemplate({
  header,
  top,
  tab,
  mobileContent,
  tabletLeftContent,
  tabletRightContent,
  onRefresh,
  isMobileRefreshing = false,
  isTabletRefreshing = false,
  tabletLeftPaneWidth,
  mobileStickyHeaderIndices = [1],
}: DetailInfoTemplateProps) {
  const styles = useStyles();
  const { isTablet } = useScaling();

  const onPullRefresh = useCallback(() => {
    if (!onRefresh) return;
    onRefresh();
  }, [onRefresh]);

  const mobileRefreshControl = onRefresh ? (
    <RefreshControl refreshing={isMobileRefreshing} onRefresh={onPullRefresh} />
  ) : undefined;
  const tabletRefreshControl = onRefresh ? (
    <RefreshControl refreshing={isTabletRefreshing} onRefresh={onPullRefresh} />
  ) : undefined;

  return (
    <Stack flex={1} backgroundColor={colors['2']}>
      {header}

      {isTablet ? (
        <Stack direction="row" flex={1} style={styles.landscapeContainer}>
          <Stack
            style={[
              styles.leftPane,
              tabletLeftPaneWidth ? { width: tabletLeftPaneWidth } : undefined,
            ]}>
            <Scroll
              style={styles.leftPaneScroll}
              nestedScrollEnabled
              contentContainerStyle={styles.leftPaneScrollContent}
              refreshControl={tabletRefreshControl}
              showsVerticalScrollIndicator={false}>
              {top}
              {tabletLeftContent}
            </Scroll>
          </Stack>

          <Stack flex={1} style={styles.rightPane}>
            {tab}
            <Scroll
              style={styles.rightPaneScroll}
              nestedScrollEnabled
              contentContainerStyle={styles.rightPaneScrollContent}
              refreshControl={tabletRefreshControl}
              showsVerticalScrollIndicator={false}>
              {tabletRightContent}
            </Scroll>
          </Stack>
        </Stack>
      ) : (
        <Scroll
          style={styles.mobileScroll}
          contentContainerStyle={styles.mobileScrollContent}
          stickyHeaderIndices={mobileStickyHeaderIndices}
          refreshControl={mobileRefreshControl}
          nestedScrollEnabled
          showsVerticalScrollIndicator={false}>
          {top}
          {tab}
          <Stack>{mobileContent}</Stack>
        </Scroll>
      )}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  landscapeContainer: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors['10'],
  },
  leftPane: {
    flexShrink: 0,
    backgroundColor: 'white',
  },
  leftPaneScroll: {
    flex: 1,
  },
  leftPaneScrollContent: {
    flexGrow: 1,
    paddingBottom: scale({ phone: 0, tablet: 100 }),
  },
  mobileScroll: {
    flex: 1,
  },
  mobileScrollContent: {
    flexGrow: 1,
    paddingBottom: scale(100),
  },
  rightPane: {
    backgroundColor: colors['1'],
  },
  rightPaneScroll: {
    flex: 1,
  },
  rightPaneScrollContent: {
    flexGrow: 1,
    paddingBottom: scale(100),
  },
}));
